import { Blockquote } from '@tiptap/extension-blockquote';
import { mergeAttributes } from '@tiptap/core';
import { NodeSelection, Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import { handleBlockquoteEnter } from '../lib/container_paragraph_keys.js';
import { handlePostEditorBackspace } from '../lib/post_editor_backspace.js';
import { sanitizePathstoneBlockquotes } from '../lib/pathstone_blockquote_content.js';
import {
  POST_EDITOR_QUOTE_VARIANTS,
  normalizePathstoneQuoteVariant,
  pathstoneQuoteHasAttribution,
} from '../lib/post_editor_constants.js';
import {
  buildBlockquoteAttrs,
  clearIndentOnBlockquoteChildren,
} from '../lib/post_block_indent.js';
import { isResourceRowNode } from '../lib/resource_row_paragraph.js';
import {
  marksToStripForRestrictedInlineLink,
  stripMarksInRange,
} from '../lib/post_editor_inline_marks.js';

export const PATHSTONE_QUOTE_VARIANTS = POST_EDITOR_QUOTE_VARIANTS;

const quoteRoleSyncKey = new PluginKey('pathstoneQuoteRoleSync');
// Walk up from the given resolved position and return the depth of the
// nearest ancestor whose node type matches `nodeName`, or -1 if none.
function findAncestorDepth($pos, nodeName) {
  for (let d = $pos.depth; d > 0; d--) {
    if ($pos.node(d).type.name === nodeName) return d;
  }
  return -1;
}

function isPathstoneQuoteBlockquote(node) {
  return node?.type?.name === 'blockquote';
}

/** True when the cursor sits in a doc-level paragraph we can wrap (not lists / resource rows). */
function shouldWrapBlockForQuote($from) {
  if (findAncestorDepth($from, 'blockquote') >= 0) return false;
  if ($from.depth < 1) return false;
  if ($from.node(1).type.name !== 'paragraph') return false;
  for (let d = 2; d <= $from.depth; d++) {
    const name = $from.node(d).type.name;
    if (name === 'listItem' || name === 'bulletList' || name === 'orderedList') return false;
    if (name === 'resourceTable' || isResourceRowNode($from.node(d))) return false;
  }
  return true;
}

function paragraphQuoteRole(paragraph) {
  return paragraph?.attrs?.psQuoteRole === 'attribution' ? 'attribution' : 'quote';
}

function syncPathstoneQuoteRoles(tr, doc) {
  let modified = false;

  doc.descendants((node, pos) => {
    if (!isPathstoneQuoteBlockquote(node)) return;

    node.forEach((child, offset) => {
      if (child.type.name !== 'paragraph') return;

      const role = paragraphQuoteRole(child);
      if (child.attrs.psQuoteRole === role) return;

      tr.setNodeMarkup(pos + 1 + offset, undefined, {
        ...child.attrs,
        psQuoteRole: role,
      });
      modified = true;
    });
  });

  return modified;
}

function blockquoteChildStartPos(blockquotePos, blockquoteNode, childIndex) {
  let pos = blockquotePos + 1;
  for (let i = 0; i < childIndex; i++) {
    pos += blockquoteNode.child(i).nodeSize;
  }
  return pos;
}

function findAttributionChildIndex(blockquoteNode) {
  let index = -1;
  blockquoteNode.forEach((child, _offset, i) => {
    if (paragraphQuoteRole(child) === 'attribution') index = i;
  });
  return index;
}

function lastQuoteChildIndex(blockquoteNode) {
  let lastIndex = 0;
  blockquoteNode.forEach((child, _offset, index) => {
    if (paragraphQuoteRole(child) === 'quote') lastIndex = index;
  });
  return lastIndex;
}

export const PathstoneBlockquote = Blockquote.extend({
  // Pull quotes: quote body + optional attribution only (no lists, headings, tables, etc.).
  content: 'paragraph+',

  addAttributes() {
    return {
      variant: {
        default: 'black',
        parseHTML: (element) => {
          const d = element.getAttribute('data-ps-quote-variant');
          if (d && POST_EDITOR_QUOTE_VARIANTS.includes(d)) return d;
          for (const c of element.classList) {
            const m = c.match(/^ps-pathstone-quote--(blue|plum|black|brick)$/);
            if (m) return m[1];
          }
          return 'black';
        },
        renderHTML: (attributes) => {
          const v = normalizePathstoneQuoteVariant(attributes.variant);
          return {
            'data-ps-quote-variant': v,
            class: `ps-pathstone-quote ps-pathstone-quote--${v}`,
          };
        },
      },
    };
  },

  renderHTML({ node, HTMLAttributes }) {
    return ['blockquote', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0];
  },

  addProseMirrorPlugins() {
    return [
      ...(this.parent?.() ?? []),
      new Plugin({
        key: quoteRoleSyncKey,
        appendTransaction(transactions, _oldState, newState) {
          if (!transactions.some((tr) => tr.docChanged)) return null;
          const tr = newState.tr;
          const sanitized = sanitizePathstoneBlockquotes(tr, newState.doc, newState.schema);
          const rolesSynced = syncPathstoneQuoteRoles(tr, tr.doc);
          if (!sanitized && !rolesSynced) return null;
          return tr;
        },
      }),
    ];
  },

  addCommands() {
    return {
      insertPathstoneQuote:
        (variant = 'black') =>
        ({ chain, state }) => {
          const v = POST_EDITOR_QUOTE_VARIANTS.includes(variant) ? variant : 'black';
          const quoteAttrs = buildBlockquoteAttrs(state, { variant: v });
          const clearChildIndentsCommand = ({ tr, dispatch }) => {
            const { $from } = tr.selection;
            const depth = findAncestorDepth($from, this.name);
            if (depth < 0) return true;
            const pos = $from.before(depth);
            const node = $from.node(depth);
            clearIndentOnBlockquoteChildren(tr, pos, node);
            if (dispatch) dispatch(tr);
            return true;
          };

          const stripLinksInBlockquoteCommand = ({ tr, dispatch, state }) => {
            const { $from } = tr.selection;
            const depth = findAncestorDepth($from, this.name);
            if (depth < 0) return true;
            const pos = $from.before(depth);
            const node = $from.node(depth);
            const from = pos + 1;
            const to = pos + node.nodeSize - 1;
            stripMarksInRange(tr, from, to, marksToStripForRestrictedInlineLink(state.schema));
            if (dispatch) dispatch(tr);
            return true;
          };

          if (!state.selection.empty) {
            return chain()
              .focus()
              .wrapIn(this.name, quoteAttrs)
              .command(clearChildIndentsCommand)
              .command(stripLinksInBlockquoteCommand)
              .run();
          }

          const { $from } = state.selection;

          // Cursor in a body paragraph: wrap the whole block (same as list turn-into).
          if (shouldWrapBlockForQuote($from)) {
            const blockPos = $from.before(1);
            return chain()
              .focus()
              .command(({ tr, dispatch }) => {
                tr.setSelection(NodeSelection.create(tr.doc, blockPos));
                if (dispatch) dispatch(tr);
                return true;
              })
              .wrapIn(this.name, quoteAttrs)
              .command(clearChildIndentsCommand)
              .command(stripLinksInBlockquoteCommand)
              .run();
          }

          // Fallback: insert a new quote after the current block instead of splitting mid-paragraph.
          const afterPos = $from.depth >= 1 ? $from.after(1) : $from.pos;
          return chain()
            .focus()
            .insertContentAt(afterPos, {
              type: this.name,
              attrs: quoteAttrs,
              content: [{ type: 'paragraph', attrs: { psQuoteRole: 'quote', indent: 0 } }],
            })
            .setTextSelection(afterPos + 2)
            .run();
        },
      addPathstoneQuoteAttribution:
        () =>
        ({ state, chain }) => {
          const { selection } = state;

          if (selection instanceof NodeSelection && selection.node.type.name === this.name) {
            const { node, from } = selection;
            if (pathstoneQuoteHasAttribution(node)) return false;
            const insertPos = from + 1 + node.content.size;
            return chain()
              .focus()
              .insertContentAt(insertPos, {
                type: 'paragraph',
                attrs: { psQuoteRole: 'attribution' },
              })
              .setTextSelection(insertPos + 1)
              .run();
          }

          const { $from } = selection;
          const blockquoteDepth = findAncestorDepth($from, this.name);
          if (blockquoteDepth < 0) return false;

          const blockquoteNode = $from.node(blockquoteDepth);
          if (pathstoneQuoteHasAttribution(blockquoteNode)) return false;

          const insertPos = $from.end(blockquoteDepth);
          return chain()
            .focus()
            .insertContentAt(insertPos, {
              type: 'paragraph',
              attrs: { psQuoteRole: 'attribution' },
            })
            .setTextSelection(insertPos + 1)
            .run();
        },
      setPathstoneQuoteVariant:
        (variant) =>
        ({ commands }) => {
          const v = POST_EDITOR_QUOTE_VARIANTS.includes(variant) ? variant : 'black';
          return commands.updateAttributes(this.name, { variant: v });
        },
    };
  },

  addKeyboardShortcuts() {
    const blockquoteName = this.name;
    const parentShortcuts = this.parent?.() ?? {};

    return {
      ...parentShortcuts,

      Enter: ({ editor }) => {
        const { state } = editor;
        const { selection } = state;
        if (!selection.empty) return false;
        const { $from } = selection;

        const blockquoteDepth = findAncestorDepth($from, blockquoteName);
        if (blockquoteDepth < 0) return false;
        if ($from.depth !== blockquoteDepth + 1) return false;

        return handleBlockquoteEnter(editor, state, $from, blockquoteDepth);
      },

      Backspace: ({ editor }) => handlePostEditorBackspace(editor.view, { key: 'Backspace' }),
    };
  },
});
